/**
 * Anime.js Async Loader and Animation Manager
 * Handles loading anime.js and managing SVG animations
 */

class AnimeLoader {
  constructor() {
    this.anime = null;
    this.isLoaded = false;
    this.loadPromise = null;
    this.animations = [];

    this.init();
  }

  async init() {
    try {
      await this.loadAnime();
      this.setupAnimations();
    } catch (error) {
      console.error("Failed to initialize animations:", error);
    }
  }

  loadAnime() {
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = new Promise((resolve, reject) => {
      // Check if anime is already loaded
      if (window.anime) {
        this.anime = window.anime;
        this.isLoaded = true;
        resolve(this.anime);
        return;
      }

      const script = document.createElement("script");
      script.src =
        "https://cdn.jsdelivr.net/npm/animejs/dist/bundles/anime.umd.min.js";
      script.async = true;

      script.onload = () => {
        this.anime = window.anime;
        this.isLoaded = true;
        console.log("✅ Anime.js loaded successfully");
        resolve(this.anime);
      };

      script.onerror = () => {
        console.error("❌ Failed to load anime.js");
        reject(new Error("Failed to load anime.js"));
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  setupAnimations() {
    if (!this.isLoaded) return;

    // SVG Draw Animations
    this.setupSVGDrawAnimations();

    // Text Animations
    this.setupTextAnimations();

    // Scroll-triggered animations
    this.setupScrollAnimations();
  }

  setupSVGDrawAnimations() {
    const { animate, svg, stagger } = this.anime;

    // Regular SVG draw animation (for .test class)
    const drawableElements = svg.createDrawable(".test");
    if (drawableElements.length > 0) {
      const drawAnimation = animate(drawableElements, {
        draw: ["0 0", "0 1", "1 1"],
        ease: "inOutQuad",
        duration: 2000,
        delay: stagger(100),
        loop: true,
      });

      this.animations.push(drawAnimation);
    }

    // Custom outline-fill animation (for .test-custom class)
    this.setupCustomOutlineFillAnimations();

    // Individual SVG animations with intersection observer
    this.setupIntersectionAnimations();
  }

  setupCustomOutlineFillAnimations() {
    const { animate, svg, createTimeline } = this.anime;

    const customElements = document.querySelectorAll(
      '.test-custom[data-animation-type="outline-fill"]'
    );

    customElements.forEach((svgElement, index) => {
      const outlinePath = svgElement.querySelector(".icon-path-outline");
      const fillPath = svgElement.querySelector(".icon-path-fill");

      if (outlinePath && fillPath) {
        // Create drawable for outline
        const drawableOutline = svg.createDrawable(outlinePath);

        if (drawableOutline.length > 0) {
          // Timeline animation: outline first, then fill
          const timeline = createTimeline({
            loop: true,
            autoplay: true,
            delay: index * 200, // Stagger delay
          });

          console.log("hi", timeline);

          // Phase 1: Draw outline
          timeline.add({
            targets: drawableOutline,
            draw: ["0 0", "0 1", "1 1"],
            duration: 1500,
            ease: "easeOutExpo",
            begin: () => {
              svgElement.classList.add("animating");
            },
          });

          // Phase 2: Fill with white
          timeline.add(
            {
              targets: fillPath,
              fillOpacity: [0, 1],
              duration: 800,
              ease: "easeOutQuart",
              complete: () => {
                svgElement.classList.remove("animating");
                svgElement.classList.add("completed");
              },
            },
            "-=200"
          ); // Start 200ms before outline finishes

          this.animations.push(timeline);
        }
      }
    });
  }

  setupIntersectionAnimations() {
    const { animate, svg } = this.anime;

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const svgElement = entry.target;

            // Handle custom outline-fill animations
            if (svgElement.classList.contains("test-custom")) {
              this.triggerCustomAnimation(svgElement);
            } else {
              // Handle regular draw animations
              const paths = svg.createDrawable(
                svgElement.querySelectorAll(".test")
              );

              if (paths.length > 0) {
                animate(paths, {
                  draw: ["0 0", "0 1", "1 1"],
                  ease: "easeOutExpo",
                  duration: 1500,
                  delay: (el, i) => i * 100,
                });
              }
            }

            this.intersectionObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );

    // Observe all SVGs with drawable elements
    this.observeAnimatableSVGs();

    // Watch for new SVGs being added to the DOM
    this.setupMutationObserver();
  }

  observeAnimatableSVGs() {
    document.querySelectorAll("svg").forEach((svg) => {
      if (
        svg.querySelector(".test") ||
        svg.querySelector(".test-custom") ||
        svg.classList.contains("test-custom")
      ) {
        // Check if not already observed
        if (!svg.dataset.animeObserved) {
          svg.dataset.animeObserved = "true";
          this.intersectionObserver.observe(svg);
        }
      }
    });
  }

  setupMutationObserver() {
    // Watch for new elements being added to the DOM
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Element node
            // Check if the node itself is an SVG
            if (node.tagName === "svg" || node.tagName === "SVG") {
              if (
                node.querySelector(".test") ||
                node.querySelector(".test-custom") ||
                node.classList.contains("test-custom")
              ) {
                if (!node.dataset.animeObserved) {
                  node.dataset.animeObserved = "true";
                  this.intersectionObserver.observe(node);
                }
              }
            }
            // Check for SVGs within the added node
            else if (node.querySelectorAll) {
              this.observeAnimatableSVGs();
            }
          }
        });
      });
    });

    // Start observing the document body for changes
    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  setupTextAnimations() {
    const { animate } = this.anime;

    // Animate text elements
    const textElements = document.querySelectorAll("[data-anime-text]");
    textElements.forEach((element) => {
      animate(element, {
        opacity: [0, 1],
        translateY: [20, 0],
        duration: 800,
        easing: "easeOutExpo",
        delay: parseInt(element.dataset.animeDelay) || 0,
      });
    });
  }

  setupScrollAnimations() {
    const { animate } = this.anime;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const element = entry.target;
            const animationType = element.dataset.animeScroll;

            switch (animationType) {
              case "fadeUp":
                animate(element, {
                  opacity: [0, 1],
                  translateY: [30, 0],
                  duration: 600,
                  easing: "easeOutExpo",
                });
                break;

              case "slideIn":
                animate(element, {
                  opacity: [0, 1],
                  translateX: [-50, 0],
                  duration: 800,
                  easing: "easeOutExpo",
                });
                break;

              case "scale":
                animate(element, {
                  opacity: [0, 1],
                  scale: [0.8, 1],
                  duration: 600,
                  easing: "easeOutBack",
                });
                break;
            }

            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    // Observe elements with scroll animations
    document.querySelectorAll("[data-anime-scroll]").forEach((element) => {
      observer.observe(element);
    });
  }

  // Public methods for manual control
  pauseAll() {
    this.animations.forEach((animation) => {
      if (animation.pause) animation.pause();
    });
  }

  playAll() {
    this.animations.forEach((animation) => {
      if (animation.play) animation.play();
    });
  }

  restartAll() {
    this.animations.forEach((animation) => {
      if (animation.restart) animation.restart();
    });
  }

  triggerCustomAnimation(svgElement) {
    const { animate, svg } = this.anime;
    const fillPath = svgElement.querySelector(".icon-path");

    if (fillPath) {
      const drawableOutline = svg.createDrawable(fillPath);

      if (drawableOutline.length > 0) {
        svgElement.classList.add("animating");

        // Phase 1: Draw outline
        animate(drawableOutline, {
          draw: ["0 0", "0 1", "1 1"],
          duration: 1200,
          ease: "easeOutExpo",
          complete: () => {
            // Phase 2: Fill with white
            animate(fillPath, {
              fillOpacity: [0, 1],
              duration: 600,
              ease: "easeOutQuart",
              complete: () => {
                svgElement.classList.remove("animating");
                svgElement.classList.add("completed");
              },
            });
          },
        });
      } else {
        console.warn("❌ No drawable outline found");
      }
    } else {
      console.warn("❌ Missing paths:", {
        outlinePath: !!outlinePath,
        fillPath: !!fillPath,
      });
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    window.animeLoader = new AnimeLoader();
  });
} else {
  window.animeLoader = new AnimeLoader();
}

// Export for manual use
export default AnimeLoader;

// Debug: Manual trigger for testing
window.testCustomAnimation = function () {
  const customElements = document.querySelectorAll(".test-custom");
  console.log("🧪 Manual test - found elements:", customElements.length);

  customElements.forEach((element, index) => {
    console.log(`Element ${index}:`, element);
    console.log("- Classes:", element.className);
    console.log("- Data attributes:", element.dataset);

    const outlinePath = element.querySelector(".icon-path-outline");
    const fillPath = element.querySelector(".icon-path-fill");
    console.log("- Outline path:", outlinePath);
    console.log("- Fill path:", fillPath);
  });

  if (window.animeLoader && customElements.length > 0) {
    customElements.forEach((element) => {
      window.animeLoader.triggerCustomAnimation(element);
    });
  } else {
    console.error("❌ AnimeLoader not available or no elements found");
    console.log("AnimeLoader:", window.animeLoader);
  }
};

// Simple test without anime loader
window.testSimpleAnimation = function () {
  const customElements = document.querySelectorAll(".test-custom");
  console.log("🎯 Simple test - found elements:", customElements.length);

  customElements.forEach((element) => {
    element.style.transform = "scale(1.2)";
    element.style.transition = "transform 0.5s ease";

    setTimeout(() => {
      element.style.transform = "scale(1)";
    }, 500);
  });
};

// Debug: Log when anime loader is ready
console.log("🎭 Anime loader script loaded");
